import { useEffect, useRef, useState } from "react";
import { Cpu, Fingerprint, HardDrive, Network, Play, RotateCcw, ShieldCheck } from "lucide-react";

const IDENTIFIERS = [
  { key: "mb", label: "Motherboard Serial", Icon: Fingerprint },
  { key: "cpu", label: "CPU ID", Icon: Cpu },
  { key: "gpu", label: "GPU ID", Icon: Cpu },
  { key: "mac", label: "MAC Address", Icon: Network },
  { key: "disk", label: "Disk Signature", Icon: HardDrive },
];

const STEPS = [
  "Rewriting motherboard serial…",
  "Masking CPU / GPU identifiers…",
  "Randomizing MAC address…",
  "Resigning disk volumes…",
  "Flushing identifier traces…",
];

const TABS = ["Spoof", "Custom", "Settings", "Devices"];

const randHex = (n) =>
  Array.from({ length: n }, () => "0123456789ABCDEF"[Math.floor(Math.random() * 16)]).join("");

const randValue = (key) => {
  if (key === "mac") return `${randHex(2)}:${randHex(2)}:${randHex(2)}:${randHex(2)}:${randHex(2)}:${randHex(2)}`;
  if (key === "disk") return `VOL-${randHex(8)}`;
  return `${randHex(4)}-${randHex(4)}-${randHex(4)}`;
};

const initialSerials = () =>
  Object.fromEntries(IDENTIFIERS.map((i) => [i.key, randValue(i.key)]));

const Toggle = ({ on, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`w-9 h-5 rounded-full relative transition-colors ${on ? "bg-[#ff4655]" : "bg-zinc-700"}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
        on ? "translate-x-[16px]" : ""
      }`}
    ></span>
  </button>
);

const WooferDemo = () => {
  const [tab, setTab] = useState("Spoof");
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [stepIdx, setStepIdx] = useState(0);
  const [serials, setSerials] = useState(initialSerials);
  const [enabled, setEnabled] = useState(Object.fromEntries(IDENTIFIERS.map((i) => [i.key, true])));
  const [settings, setSettings] = useState({ boot: false, clean: true, stealth: true });
  const scrambler = useRef(null);
  const timers = useRef([]);

  useEffect(() => {
    return () => {
      clearInterval(scrambler.current);
      timers.current.forEach(clearTimeout);
    };
  }, []);

  const startSpoof = () => {
    if (phase === "running") return;
    setPhase("running");
    setStepIdx(0);
    scrambler.current = setInterval(() => {
      setSerials((prev) => {
        const next = { ...prev };
        IDENTIFIERS.forEach((i) => {
          if (enabled[i.key]) next[i.key] = randValue(i.key);
        });
        return next;
      });
    }, 70);
    STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setStepIdx(i + 1), (i + 1) * 900));
    });
    timers.current.push(
      setTimeout(() => {
        clearInterval(scrambler.current);
        setPhase("done");
      }, STEPS.length * 900 + 400)
    );
  };

  const reset = () => {
    clearInterval(scrambler.current);
    timers.current.forEach(clearTimeout);
    setPhase("idle");
    setStepIdx(0);
    setSerials(initialSerials());
  };

  const statusText =
    phase === "idle"
      ? "Status: Ready to spoof serials…"
      : phase === "running"
        ? `Status: ${STEPS[Math.min(stepIdx, STEPS.length - 1)]}`
        : "Status: Fresh hardware profile active";

  const progress = phase === "done" ? 100 : phase === "running" ? Math.round((stepIdx / STEPS.length) * 100) : 0;

  return (
    <div data-testid="woofer-demo" className="mt-16 border border-red-500/30 bg-[#0a0b0f] relative overflow-hidden">
      {/* window chrome */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-red-500/20 bg-[#0e0f14]">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-zinc-700"></span>
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff4655]"></span>
          <span className="ml-3 font-display text-lg tracking-wider text-white uppercase">Woofer</span>
          <span className="font-mono-x text-[9px] text-zinc-500 uppercase tracking-widest hidden sm:inline">
            Hardware Identity, Rewritten
          </span>
        </div>
        <span className="font-mono-x text-[9px] uppercase tracking-widest text-[#ff4655] border border-[#ff4655]/40 px-2 py-1">
          Interface Preview — Simulated Demo
        </span>
      </div>

      {/* tabs */}
      <div className="flex justify-center gap-1 px-4 pt-5" data-testid="demo-tabs">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`demo-tab-${t.toLowerCase()}`}
            onClick={() => setTab(t)}
            className={`px-5 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
              tab === t ? "bg-[#ff4655] text-white" : "bg-zinc-900 text-zinc-400 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="p-6 md:p-10 min-h-[340px] flex flex-col">
        {tab === "Spoof" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 w-full max-w-3xl">
              {IDENTIFIERS.map(({ key, label, Icon }) => (
                <div key={key} className="p-3 bg-zinc-900/70 border border-zinc-800 text-center">
                  <Icon className={`w-4 h-4 mx-auto mb-2 ${phase !== "idle" && enabled[key] ? "text-[#ff4655]" : "text-zinc-500"}`} />
                  <div className="text-[9px] font-mono-x uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
                  <div
                    data-testid={`demo-serial-${key}`}
                    className={`font-mono-x text-[10px] ${phase === "running" && enabled[key] ? "text-[#ff4655]" : "text-zinc-200"}`}
                  >
                    {serials[key]}
                  </div>
                </div>
              ))}
            </div>

            <div className="w-full max-w-xl h-1 bg-zinc-800 overflow-hidden">
              <div
                data-testid="demo-progress"
                className="h-full bg-[#ff4655] transition-all duration-500"
                style={{ width: `${progress}%` }}
              ></div>
            </div>

            <button
              type="button"
              data-testid="demo-start-btn"
              onClick={phase === "done" ? reset : startSpoof}
              disabled={phase === "running"}
              className="px-10 py-4 bg-[#ff4655] hover:bg-white hover:text-black disabled:opacity-60 text-white font-extrabold uppercase text-xs tracking-widest transition-colors skew-x-[-8deg] flex items-center gap-2"
            >
              <span className="inline-flex skew-x-[8deg] items-center gap-2">
                {phase === "done" ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {phase === "running" ? "Spoofing…" : phase === "done" ? "Run Demo Again" : "Start Auto-Spoofing"}
              </span>
            </button>

            <p data-testid="demo-status" className={`font-mono-x text-xs ${phase === "done" ? "text-[#ff4655]" : "text-zinc-400"}`}>
              {statusText}
            </p>

            {phase === "done" && (
              <a
                href="#pricing"
                data-testid="demo-get-woofer-btn"
                onClick={() => window.dispatchEvent(new CustomEvent("scythe:show-woofer"))}
                className="font-mono-x text-xs uppercase tracking-widest text-white border border-[#ff4655]/50 px-6 py-3 hover:bg-[#ff4655] transition-colors flex items-center gap-2"
              >
                <ShieldCheck className="w-4 h-4 text-[#ff4655]" /> Unlock Woofer — from €25
              </a>
            )}
          </div>
        )}

        {tab === "Custom" && (
          <div className="flex-1 flex flex-col justify-center max-w-xl mx-auto w-full gap-3">
            <p className="font-mono-x text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Choose which identifiers the spoof covers
            </p>
            {IDENTIFIERS.map(({ key, label, Icon }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-zinc-900/70 border border-zinc-800">
                <span className="flex items-center gap-3 text-xs text-white font-bold uppercase tracking-wider">
                  <Icon className="w-4 h-4 text-[#ff4655]" /> {label}
                </span>
                <Toggle
                  testid={`demo-custom-toggle-${key}`}
                  on={enabled[key]}
                  onClick={() => setEnabled((p) => ({ ...p, [key]: !p[key] }))}
                />
              </div>
            ))}
          </div>
        )}

        {tab === "Settings" && (
          <div className="flex-1 flex flex-col justify-center max-w-xl mx-auto w-full gap-3">
            <p className="font-mono-x text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Runtime options</p>
            {[
              { key: "boot", label: "Apply Profile On Boot" },
              { key: "clean", label: "Auto-Clean Identifier Traces" },
              { key: "stealth", label: "Low-Footprint Mode" },
            ].map((s) => (
              <div key={s.key} className="flex items-center justify-between p-4 bg-zinc-900/70 border border-zinc-800">
                <span className="text-xs text-white font-bold uppercase tracking-wider">{s.label}</span>
                <Toggle
                  testid={`demo-setting-${s.key}`}
                  on={settings[s.key]}
                  onClick={() => setSettings((p) => ({ ...p, [s.key]: !p[s.key] }))}
                />
              </div>
            ))}
          </div>
        )}

        {tab === "Devices" && (
          <div className="flex-1 flex flex-col justify-center max-w-2xl mx-auto w-full gap-3">
            <p className="font-mono-x text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Detected devices</p>
            {IDENTIFIERS.map(({ key, label, Icon }) => (
              <div key={key} className="flex items-center justify-between p-4 bg-zinc-900/70 border border-zinc-800">
                <span className="flex items-center gap-3 text-xs text-white font-bold uppercase tracking-wider">
                  <Icon className="w-4 h-4 text-[#ff4655]" /> {label}
                </span>
                <span className="flex items-center gap-3">
                  <span className="font-mono-x text-[10px] text-zinc-400 hidden sm:inline">{serials[key]}</span>
                  <span
                    data-testid={`demo-device-status-${key}`}
                    className={`font-mono-x text-[9px] uppercase tracking-widest px-2 py-1 ${
                      phase === "done" && enabled[key]
                        ? "text-[#ff4655] border border-[#ff4655]/50"
                        : "text-zinc-500 border border-zinc-700"
                    }`}
                  >
                    {phase === "done" && enabled[key] ? "Spoofed" : "Original"}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WooferDemo;
